import React, { useState, useEffect } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';
import { debugLogger } from '@/utils/debugLogger';

/**
 * Debug overlay component - shows logs and camera pipeline data
 * Enabled via ?debug=1 or localStorage.debugMode = 'true'
 */
export const DebugOverlay: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [logs, setLogs] = useState(debugLogger.getLogs());

  // Refresh logs periodically
  useEffect(() => {
    if (!debugLogger.getEnabled()) return;

    const interval = setInterval(() => {
      setLogs(debugLogger.getLogs());
    }, 500);

    return () => clearInterval(interval);
  }, []);

  if (!debugLogger.getEnabled()) {
    return null;
  }

  const summary = debugLogger.getDebugSummary();

  return (
    <>
      {/* Minimized button */}
      {isMinimized && (
        <button
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-4 right-4 z-[9999] bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-3 py-2 rounded-lg shadow-lg flex items-center gap-2"
        >
          🔍 DEBUG ({logs.length})
        </button>
      )}

      {/* Full overlay */}
      {!isMinimized && (
        <div className="fixed bottom-4 right-4 z-[9999] w-96 max-h-[80vh] bg-gray-900 border-2 border-purple-500 rounded-lg shadow-2xl flex flex-col">
          {/* Header */}
          <div className="bg-purple-600 text-white p-3 flex items-center justify-between rounded-t-lg">
            <div className="font-bold text-sm">🔍 Debug Overlay</div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(true)}
                className="p-1 hover:bg-purple-700 rounded"
                title="Minimize"
              >
                <Minimize2 size={16} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-purple-700 rounded"
                title="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 text-xs font-mono space-y-4">
            {/* Camera Capture Data */}
            {summary.cameraCapture && (
              <div className="bg-blue-900/30 border border-blue-500/50 rounded p-2">
                <div className="font-bold text-blue-300 mb-1">📷 Camera Capture</div>
                <div className="text-gray-300 space-y-1">
                  <div>webPath: {summary.cameraCapture.webPath?.substring(0, 50) || 'N/A'}...</div>
                  <div>format: {summary.cameraCapture.format || 'N/A'}</div>
                  <div>width: {summary.cameraCapture.width || 'N/A'}</div>
                  <div>height: {summary.cameraCapture.height || 'N/A'}</div>
                  <div>requestedDirection: {summary.cameraCapture.requestedDirection || 'N/A'}</div>
                  <div>exif.orientation: {summary.cameraCapture.exif?.Orientation || 'N/A'}</div>
                </div>
              </div>
            )}

            {/* Normalized Photo Data */}
            {summary.normalizedPhoto && (
              <div className="bg-green-900/30 border border-green-500/50 rounded p-2">
                <div className="font-bold text-green-300 mb-1">🖼️ Normalized Photo</div>
                <div className="text-gray-300 space-y-1">
                  <div>type: {summary.normalizedPhoto.type || 'N/A'}</div>
                  <div>size: {summary.normalizedPhoto.size ? `${Math.round(summary.normalizedPhoto.size / 1024)}KB` : 'N/A'}</div>
                  <div>width: {summary.normalizedPhoto.width || 'N/A'}</div>
                  <div>height: {summary.normalizedPhoto.height || 'N/A'}</div>
                  <div>mirroredApplied: {summary.normalizedPhoto.mirroredApplied !== undefined ? String(summary.normalizedPhoto.mirroredApplied) : 'N/A'}</div>
                </div>
              </div>
            )}

            {/* Upload Response */}
            {summary.uploadResponse && (
              <div className="bg-yellow-900/30 border border-yellow-500/50 rounded p-2">
                <div className="font-bold text-yellow-300 mb-1">☁️ Upload Response</div>
                <div className="text-gray-300 space-y-1">
                  {summary.uploadResponse.success ? (
                    <>
                      <div className="text-green-400">✅ Success</div>
                      <div>URL: {summary.uploadResponse.url?.substring(0, 50) || 'N/A'}...</div>
                    </>
                  ) : (
                    <>
                      <div className="text-red-400">❌ Failed</div>
                      <div>Error: {summary.uploadResponse.error || 'Unknown'}</div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Logs */}
            <div className="bg-gray-800/50 border border-gray-600 rounded p-2">
              <div className="font-bold text-gray-300 mb-2">📋 Logs (last 30)</div>
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {summary.logs.length === 0 ? (
                  <div className="text-gray-500">No logs yet</div>
                ) : (
                  summary.logs.map((log, idx) => (
                    <div
                      key={idx}
                      className={`text-xs ${
                        log.level === 'error' ? 'text-red-400' :
                        log.level === 'warn' ? 'text-yellow-400' :
                        log.level === 'info' ? 'text-blue-400' :
                        'text-gray-400'
                      }`}
                    >
                      <span className="text-gray-500">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </span>
                      {' '}
                      <span className="font-bold">[{log.level.toUpperCase()}]</span>
                      {' '}
                      {log.message}
                      {log.data && (
                        <div className="ml-4 mt-1 text-gray-500 text-xs">
                          {typeof log.data === 'object' ? JSON.stringify(log.data, null, 2) : String(log.data)}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-800 p-2 flex justify-between items-center rounded-b-lg border-t border-gray-700">
            <button
              onClick={() => debugLogger.clearLogs()}
              className="text-xs text-gray-400 hover:text-white"
            >
              Clear Logs
            </button>
            <button
              onClick={() => debugLogger.setEnabled(false)}
              className="text-xs text-gray-400 hover:text-white"
            >
              Disable Debug
            </button>
          </div>
        </div>
      )}
    </>
  );
};
